// Destination configuration for TravelDestinationPage.
// Each slug defines: countries to filter (lowercase, case-insensitive match),
// localized hero copy, flag emoji and accent color.
// Used by /travel/:slug routes (espana, europa, japon, usa, ...).

export const DESTINATIONS = {
  espana: {
    slug: "espana",
    aliases: ["spain", "agencias"],
    // Filter rule: single-country plan whose only country matches one of these names
    countries: ["spain", "españa", "espana"],
    flag: "🇪🇸",
    accent: "#FF6B35", // orange (Spain flag)
    h1: {
      es: ["Datos móviles en", "España", "para tus viajeros."],
      en: ["Mobile data in", "Spain", "for your travellers."],
    },
    sub: {
      es: "eSIM activable al instante con QR. Tus clientes llegan con datos antes incluso de salir del aeropuerto. Sin tarjetas físicas, sin esperas, sin sorpresas.",
      en: "Instant-activation eSIM with QR. Your customers land with mobile data before even leaving the airport. No physical SIM, no waiting, no surprises.",
    },
    seo_title: {
      es: "eSIM España para agencias de viajes",
      en: "Spain eSIM for travel agencies",
    },
    seo_description: {
      es: "eSIM con datos en España para tus clientes. Activación instantánea por QR, cobertura 5G nacional, sin tarjetas físicas. -15% con CANARIAS25.",
      en: "Mobile data eSIM for your customers travelling to Spain. Instant QR activation, nationwide 5G coverage, no physical SIM. 15% off with CANARIAS25.",
    },
    seo_keywords: {
      es: "eSIM España, eSIM Madrid, eSIM Barcelona, datos móviles España, eSIM viajeros España, agencias eSIM España",
      en: "eSIM Spain, eSIM Madrid Barcelona, mobile data Spain, travel eSIM Spain, Spain eSIM agencies",
    },
  },
  europa: {
    slug: "europa",
    aliases: ["europe"],
    // Regional plans whose countries list contains europe/europa
    countries: ["europe", "europa"],
    regional: true, // also matches multi-country plans that include any of these
    flag: "🇪🇺",
    accent: "#003399",
    h1: {
      es: ["Datos móviles en", "Europa", "para tus viajeros."],
      en: ["Mobile data in", "Europe", "for your travellers."],
    },
    sub: {
      es: "Un solo plan para todo el continente. Tus clientes navegan en 35+ países europeos con la misma eSIM, sin recargos por roaming.",
      en: "One single plan for the whole continent. Your customers surf in 35+ European countries with the same eSIM, with no roaming surcharges.",
    },
    seo_title: {
      es: "eSIM Europa para agencias de viajes",
      en: "Europe eSIM for travel agencies",
    },
    seo_description: {
      es: "eSIM con datos en Europa para tus viajeros: una sola tarifa para 35+ países. Sin roaming, sin recargos. Activación instantánea por QR.",
      en: "Mobile data eSIM for Europe travellers: one tariff for 35+ countries. No roaming, no surcharges. Instant QR activation.",
    },
    seo_keywords: {
      es: "eSIM Europa, datos móviles Europa, eSIM 35 países, eSIM viaje Europa, agencias eSIM Europa, evitar roaming Europa",
      en: "eSIM Europe, mobile data Europe, eSIM 35 countries, travel eSIM Europe, agency eSIM Europe",
    },
  },
  japon: {
    slug: "japon",
    aliases: ["japan"],
    countries: ["japan", "japón", "japon"],
    flag: "🇯🇵",
    accent: "#BC002D",
    h1: {
      es: ["Datos móviles en", "Japón", "para tus viajeros."],
      en: ["Mobile data in", "Japan", "for your travellers."],
    },
    sub: {
      es: "El destino más exigente en conectividad ya no es un problema. Tu cliente llega a Tokio, Kyoto u Osaka con datos al instante.",
      en: "The most demanding destination for connectivity is no longer a problem. Your customer lands in Tokyo, Kyoto or Osaka with instant data.",
    },
    seo_title: {
      es: "eSIM Japón para agencias de viajes",
      en: "Japan eSIM for travel agencies",
    },
    seo_description: {
      es: "eSIM con datos en Japón para tus viajeros. Cobertura en Tokio, Kyoto, Osaka. Activación instantánea por QR. Sin sorpresas de roaming.",
      en: "Mobile data eSIM for Japan travellers. Coverage in Tokyo, Kyoto, Osaka. Instant QR activation. No roaming surprises.",
    },
    seo_keywords: {
      es: "eSIM Japón, eSIM Tokio, datos móviles Japón, eSIM viaje Japón, agencias eSIM Japón",
      en: "eSIM Japan, eSIM Tokyo, mobile data Japan, travel eSIM Japan",
    },
  },
  usa: {
    slug: "usa",
    aliases: ["estados-unidos", "us"],
    countries: ["united states", "usa", "estados unidos", "ee.uu.", "ee. uu."],
    flag: "🇺🇸",
    accent: "#3C3B6E",
    h1: {
      es: ["Datos móviles en", "Estados Unidos", "para tus viajeros."],
      en: ["Mobile data in", "the United States", "for your travellers."],
    },
    sub: {
      es: "5G en costa a costa. De Nueva York a Los Ángeles, tus clientes no pierden la señal ni una vez.",
      en: "5G coast to coast. From New York to Los Angeles, your customers don't lose signal once.",
    },
    seo_title: {
      es: "eSIM EE.UU. para agencias de viajes",
      en: "USA eSIM for travel agencies",
    },
    seo_description: {
      es: "eSIM con datos en EE.UU. para tus viajeros. 5G de costa a costa, sin recargos. Activación instantánea por QR.",
      en: "Mobile data eSIM for USA travellers. 5G coast to coast, no surcharges. Instant QR activation.",
    },
    seo_keywords: {
      es: "eSIM EEUU, eSIM Estados Unidos, eSIM Nueva York, datos móviles USA, eSIM viaje USA",
      en: "eSIM USA, eSIM New York, mobile data USA, travel eSIM USA",
    },
  },
};

// ---- B2C consumer destinations (SEO landings) ----
// Generated via factory: consumer-facing copy (traveler, not agency).
const mkDest = ({ slug, aliases = [], countries, flag, accent, name, nameEn, cities, citiesEn }) => ({
  slug,
  aliases,
  countries,
  flag,
  accent,
  b2c: true,
  name: { es: name, en: nameEn },
  h1: {
    es: ["Internet en", name, "desde que aterrizas."],
    en: ["Mobile data in", nameEn, "from the moment you land."],
  },
  sub: {
    es: `eSIM de datos para tu viaje a ${name}. Actívala con un QR en 2 minutos y navega en ${cities} sin roaming ni sorpresas en la factura.`,
    en: `Travel data eSIM for ${nameEn}. Activate with a QR in 2 minutes and browse in ${citiesEn} with no roaming fees or bill surprises.`,
  },
  seo_title: {
    es: `eSIM ${name}: internet para tu viaje sin roaming`,
    en: `${nameEn} eSIM: travel data without roaming fees`,
  },
  seo_description: {
    es: `eSIM con datos en ${name}: activación instantánea por QR, cobertura en ${cities}. Sin roaming, sin tarjeta física. -15% con CANARIAS25.`,
    en: `Data eSIM for ${nameEn}: instant QR activation, coverage in ${citiesEn}. No roaming, no physical SIM. 15% off with CANARIAS25.`,
  },
  seo_keywords: {
    es: `eSIM ${name}, internet en ${name}, datos móviles ${name}, eSIM viaje ${name}, roaming ${name}, tarjeta SIM ${name}`,
    en: `eSIM ${nameEn}, internet in ${nameEn}, mobile data ${nameEn}, travel eSIM ${nameEn}`,
  },
});

const B2C_DESTINATIONS = [
  mkDest({ slug: "canarias", aliases: ["canary-islands", "esim-canarias", "tenerife", "lanzarote", "gran-canaria", "fuerteventura", "esim-tenerife", "esim-lanzarote"], countries: ["spain", "españa", "espana"], flag: "🇮🇨", accent: "#7DD500", name: "Canarias", nameEn: "the Canary Islands", cities: "Tenerife, Gran Canaria, Lanzarote y Fuerteventura", citiesEn: "Tenerife, Gran Canaria, Lanzarote and Fuerteventura" }),
  mkDest({ slug: "marruecos", aliases: ["morocco", "esim-marruecos"], countries: ["marruecos"], flag: "🇲🇦", accent: "#E4572E", name: "Marruecos", nameEn: "Morocco", cities: "Marrakech, Fez y Casablanca", citiesEn: "Marrakech, Fez and Casablanca" }),
  mkDest({ slug: "tailandia", aliases: ["thailand", "esim-tailandia"], countries: ["tailandia"], flag: "🇹🇭", accent: "#41B6E6", name: "Tailandia", nameEn: "Thailand", cities: "Bangkok, Phuket y Chiang Mai", citiesEn: "Bangkok, Phuket and Chiang Mai" }),
  mkDest({ slug: "mexico", aliases: ["esim-mexico"], countries: ["méxico", "mexico"], flag: "🇲🇽", accent: "#00A86B", name: "México", nameEn: "Mexico", cities: "CDMX, Cancún y Riviera Maya", citiesEn: "Mexico City, Cancun and Riviera Maya" }),
  mkDest({ slug: "reino-unido", aliases: ["uk", "esim-reino-unido", "inglaterra"], countries: ["reino unido"], flag: "🇬🇧", accent: "#C8102E", name: "Reino Unido", nameEn: "the UK", cities: "Londres, Edimburgo y Manchester", citiesEn: "London, Edinburgh and Manchester" }),
  mkDest({ slug: "argentina", aliases: ["esim-argentina"], countries: ["argentina"], flag: "🇦🇷", accent: "#75AADB", name: "Argentina", nameEn: "Argentina", cities: "Buenos Aires, Mendoza y la Patagonia", citiesEn: "Buenos Aires, Mendoza and Patagonia" }),
  mkDest({ slug: "republica-dominicana", aliases: ["punta-cana", "esim-republica-dominicana"], countries: ["república dominicana", "r. dominicana"], flag: "🇩🇴", accent: "#3E8FD8", name: "República Dominicana", nameEn: "the Dominican Republic", cities: "Punta Cana, Santo Domingo y Samaná", citiesEn: "Punta Cana, Santo Domingo and Samaná" }),
  mkDest({ slug: "cuba", aliases: ["esim-cuba"], countries: ["cuba"], flag: "🇨🇺", accent: "#CF6679", name: "Cuba", nameEn: "Cuba", cities: "La Habana, Varadero y Trinidad", citiesEn: "Havana, Varadero and Trinidad" }),
  mkDest({ slug: "egipto", aliases: ["egypt", "esim-egipto"], countries: ["egipto"], flag: "🇪🇬", accent: "#D4A017", name: "Egipto", nameEn: "Egypt", cities: "El Cairo, Luxor y el Mar Rojo", citiesEn: "Cairo, Luxor and the Red Sea" }),
  mkDest({ slug: "dubai", aliases: ["emiratos", "emiratos-arabes", "esim-dubai"], countries: ["emiratos árabes unidos", "emiratos árabes"], flag: "🇦🇪", accent: "#00A19C", name: "Dubái y Emiratos", nameEn: "Dubai & UAE", cities: "Dubái y Abu Dabi", citiesEn: "Dubai and Abu Dhabi" }),
  mkDest({ slug: "china", aliases: ["esim-china"], countries: ["china"], flag: "🇨🇳", accent: "#E64B3C", name: "China", nameEn: "China", cities: "Pekín, Shanghái y Cantón", citiesEn: "Beijing, Shanghai and Guangzhou" }),
  mkDest({ slug: "vietnam", aliases: ["esim-vietnam"], countries: ["vietnam"], flag: "🇻🇳", accent: "#E8B23A", name: "Vietnam", nameEn: "Vietnam", cities: "Hanói, Ho Chi Minh y la bahía de Ha Long", citiesEn: "Hanoi, Ho Chi Minh City and Ha Long Bay" }),
  mkDest({ slug: "brasil", aliases: ["brazil", "esim-brasil"], countries: ["brasil"], flag: "🇧🇷", accent: "#2FB457", name: "Brasil", nameEn: "Brazil", cities: "Río de Janeiro, São Paulo y Salvador", citiesEn: "Rio de Janeiro, São Paulo and Salvador" }),
  mkDest({ slug: "colombia", aliases: ["esim-colombia"], countries: ["colombia"], flag: "🇨🇴", accent: "#F2C230", name: "Colombia", nameEn: "Colombia", cities: "Bogotá, Medellín y Cartagena", citiesEn: "Bogotá, Medellín and Cartagena" }),
  mkDest({ slug: "peru", aliases: ["esim-peru"], countries: ["perú", "peru"], flag: "🇵🇪", accent: "#E05263", name: "Perú", nameEn: "Peru", cities: "Lima, Cusco y Machu Picchu", citiesEn: "Lima, Cusco and Machu Picchu" }),
  mkDest({ slug: "canada", aliases: ["esim-canada"], countries: ["canadá", "canada"], flag: "🇨🇦", accent: "#E4572E", name: "Canadá", nameEn: "Canada", cities: "Toronto, Vancouver y Montreal", citiesEn: "Toronto, Vancouver and Montreal" }),
  mkDest({ slug: "australia", aliases: ["esim-australia"], countries: ["australia"], flag: "🇦🇺", accent: "#3E8FD8", name: "Australia", nameEn: "Australia", cities: "Sídney, Melbourne y Brisbane", citiesEn: "Sydney, Melbourne and Brisbane" }),
  mkDest({ slug: "italia", aliases: ["italy", "esim-italia"], countries: ["italia"], flag: "🇮🇹", accent: "#2FB457", name: "Italia", nameEn: "Italy", cities: "Roma, Milán y Venecia", citiesEn: "Rome, Milan and Venice" }),
  mkDest({ slug: "francia", aliases: ["france", "esim-francia"], countries: ["francia"], flag: "🇫🇷", accent: "#4D7FD1", name: "Francia", nameEn: "France", cities: "París, Niza y Lyon", citiesEn: "Paris, Nice and Lyon" }),
  mkDest({ slug: "portugal", aliases: ["esim-portugal"], countries: ["portugal"], flag: "🇵🇹", accent: "#2FB457", name: "Portugal", nameEn: "Portugal", cities: "Lisboa, Oporto y el Algarve", citiesEn: "Lisbon, Porto and the Algarve" }),
  mkDest({ slug: "suiza", aliases: ["switzerland", "esim-suiza"], countries: ["suiza"], flag: "🇨🇭", accent: "#E05263", name: "Suiza", nameEn: "Switzerland", cities: "Zúrich, Ginebra y Berna", citiesEn: "Zurich, Geneva and Bern" }),
];
B2C_DESTINATIONS.forEach((d) => { DESTINATIONS[d.slug] = d; });

// Resolve a slug or alias to a destination
export const resolveDestination = (slugOrAlias) => {
  const key = (slugOrAlias || "").toLowerCase();
  if (DESTINATIONS[key]) return DESTINATIONS[key];
  for (const k of Object.keys(DESTINATIONS)) {
    if ((DESTINATIONS[k].aliases || []).includes(key)) return DESTINATIONS[k];
  }
  return DESTINATIONS.espana; // default fallback
};

// European country whitelist used to identify regional bundles whose
// countries list contains individual country names instead of "Europe".
const EU_COUNTRIES = new Set([
  "alemania", "austria", "belgica", "bélgica", "bulgaria", "chipre",
  "croacia", "dinamarca", "eslovaquia", "eslovenia", "españa", "spain",
  "estonia", "finlandia", "francia", "grecia", "hungria", "hungría",
  "irlanda", "islandia", "italia", "letonia", "liechtenstein", "lituania",
  "luxemburgo", "malta", "noruega", "paises bajos", "países bajos",
  "polonia", "portugal", "reino unido", "republica checa", "república checa",
  "rumania", "rumanía", "suecia", "suiza", "turquia", "turquía",
  "germany", "belgium", "denmark", "estonia", "france", "greece",
  "ireland", "iceland", "italy", "latvia", "lithuania", "luxembourg",
  "netherlands", "poland", "romania", "sweden", "switzerland",
  "united kingdom", "uk", "czech republic", "slovakia", "slovenia",
]);

// Lowercase set comparison for a plan
export const matchesDestination = (plan, dest) => {
  const cs = (plan.countries || []).map((c) => (c || "").toLowerCase().trim());
  if (cs.length === 0) return false;
  // For single-country bundles (España, Japón, USA): require exact match and only-one-country
  if (!dest.regional) {
    if (cs.length !== 1) return false;
    return dest.countries.some((needle) => cs[0] === needle);
  }
  // Regional (Europe): match if
  //   (1) the list contains the literal region name ('europe'/'europa'), OR
  //   (2) it is a multi-country bundle with ≥10 European countries from the whitelist.
  // This catches TravelRoam regional packs that ship as a list of specific EU countries.
  if (dest.countries.some((needle) => cs.includes(needle))) return true;
  if (cs.length >= 10) {
    const euHits = cs.filter((c) => EU_COUNTRIES.has(c)).length;
    if (euHits >= 10) return true;
  }
  return false;
};

export default DESTINATIONS;
